import CustomList from '@/components/global/list/CustomList.jsx';
import { FunctionField, TopToolbar, usePermissions, useTranslate, useResourceContext, useGetOne, useGetIdentity, Edit } from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { useTheme, useMediaQuery, Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DeviceCollectionPointField from '@/components/device/DeviceCollectionPointField.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import { useDrawer } from '@/composables/useDrawer';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle';
import DeviceServiceStatusField from '@/components/device/DeviceServiceStatusField';
import { ServiceRequestStatusField } from '@/components/service/list/ServiceRequestStatusField';
import { ServiceRequestIdField } from '@/components/service/list/ServiceRequestIdField';
import { ServiceRequestUsersField } from '@/components/service/list/ServiceRequestUsersField';
import UnassignedField from '@/components/global/fields/UnassignedField.jsx';
import { serviceRequestPriorityChoices, serviceRequestSourceTypeChoices, serviceRequestStatusChoices } from '@/components/global/options/selectOptions.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import ServiceDate from '@/components/service/ServiceDate';
import { startOfDay, isBefore } from 'date-fns';
import { Palette } from '@/themes/themeOverrides/Palette';
import { DueDateFilter } from '@/components/global/filters/DueDateFilter.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { ServiceForm } from '@/components/service/forms/ServiceForm.jsx';
import { ServiceShow } from '@/components/service/ServiceShow.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer.jsx';
import { styled } from '@mui/material/styles';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { ServiceBulkActionButtons } from '@/components/global/buttons/bulkButtons.jsx';
import { ServiceBulkActionForm } from '@/components/service/ServiceBulkActionForm.jsx';
import { ServiceInspectionMap } from '@/components/service/map/ServiceInspectionMap.jsx';
import MapIcon from '@/assets/icons/map-icon.svg?react';
import TableIcon from '@/assets/icons/table-icon.svg?react';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';

const searchBar = <SearchBar key='search' source='value' alwaysOn />;

const getServiceRegularFilters = (isMobile, translate) => [
    <DueDateFilter key='filter_dueDate' fromSource='dueDate_gte' toSource='dueDate_lte' alwaysOn />,
    <CustomSelectArrayInput
        key='filter_status'
        source='status_in'
        label={translate('common.status')}
        choices={serviceRequestStatusChoices(translate)}
        alwaysOn
        forceDesktopOnMobile={true}
    />,
    <CustomSelectArrayInput
        key='filter_priority'
        source='priority_in'
        label={translate('common.priorityLabel')}
        choices={serviceRequestPriorityChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <ReferenceArrayFilter
        key='collectionPoint->id_in'
        source='collectionPoint->id_in'
        label={translate('common.collectionPoint')}
        optionText={choice => choice.customId}
        reference='collection-point'
        searchFields={['customId']}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <FromToFilterComponent
        key='createdAt'
        source='createdAt_range'
        label={translate('common.createdAtFilter')}
        type='date'
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <FromToFilterComponent
        key='updatedAt'
        source='updatedAt_range'
        label={translate('common.updatedAtFilter')}
        type='date'
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <CustomSelectArrayInput
        key='sourceType'
        source='sourceType_in'
        label={translate('common.source')}
        choices={serviceRequestSourceTypeChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
];

const getAllFilters = (isMobile, translate) => [
    ...getServiceRegularFilters(isMobile, translate),
    <ReferenceArrayFilter
        key='filter_user'
        source='coordinatorId+servicemanId_in'
        label={translate('resources.serviceRequest.labels.serviceman')}
        reference='user'
        optionText={choice => `${choice.firstName} ${choice.lastName}`}
        searchFields={['firstName', 'lastName']}
        filter={{ role_in: ['ROLE_SERVICE_MASTER', 'ROLE_SERVICE'] }}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
];

const StyledServiceList = ({ children, ...props }) => {
    return (
        <CustomList {...props}>
            <Box className='responsive-table service-list'>{children}</Box>
        </CustomList>
    );
};

const StyledViewToggle = styled(ToggleButtonGroup)(({ theme }) => ({
    '&.MuiToggleButtonGroup-root': {
        borderRadius: '12px',
    },
    '& .MuiButtonBase-root': {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: 400,
        borderRadius: '10px 0 0 10px',
        padding: '10px 18px',
        border: `2px solid ${Palette.gray.gray200}`,
        backgroundColor: Palette.gray.gray50,
        color: Palette.gray.gray900,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: Palette.white,
            boxShadow: Palette.boxShadow.elevation2,
            '& .toggle-button-content': {
                opacity: 1,
            },
        },
        '& + .MuiButtonBase-root': {
            borderRadius: '0 10px 10px 0',
            borderLeft: `1px solid ${Palette.gray.gray200}`,
        },
        '&.Mui-selected': {
            backgroundColor: Palette.white,
            boxShadow: Palette.boxShadow.elevation2,
            '& .toggle-button-content': {
                opacity: 1,
            },
        },
        '& .toggle-button-content': {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            opacity: 0.5,
            [theme.breakpoints.down('md')]: {
                fontSize: '0px',
                width: '20px',
            },
        },
        '& svg': {
            width: '20px',
            height: '20px',
            flexShrink: 0,
        },
    },
}));

export const ServiceInspectionList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const { openDrawer } = useDrawer();
    const { data, isLoading: isLoadingIdentity } = useGetIdentity();
    const translate = useTranslate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [view, setView] = useState('list');

    const userEmail = data?.id;
    const isServiceRegular = permissions.isService && !permissions.isServiceMaster;
    const listFilter = useMemo(
        () => (isServiceRegular ? { 'serviceman.email': userEmail, withAggregates: true } : { withAggregates: true }),
        [isServiceRegular, userEmail],
    );

    const handleRowClick = id => {
        openDrawer(id, 'service-inspection-edit');
        return false;
    };

    useEffect(() => {
        const handleResetToList = () => {
            setView('list');
        };
        globalThis.addEventListener('serviceInspectionView:resetToList', handleResetToList);
        return () => globalThis.removeEventListener('serviceInspectionView:resetToList', handleResetToList);
    }, []);

    const filters = useMemo(
        () => (permissions.isAdmin || permissions.isServiceMaster ? getAllFilters(isMobile, translate) : getServiceRegularFilters(isMobile, translate)),
        [isMobile, permissions.isAdmin, permissions.isServiceMaster, translate],
    );

    if (isLoading || isLoadingIdentity) return null;

    const bulkActionButtons =
        permissions.isAdmin || permissions.isService ? <ServiceBulkActionButtons buttons={[<ServiceBulkActionForm key='bulk-form' />]} /> : false;

    return (
        <StyledServiceList
            filters={[{ ...searchBar, props: { ...searchBar.props, label: translate('common.search') } }, ...filters]}
            filterDefaultValues={
                isDesktop
                    ? {
                          status_in: ['new', 'failedContact', 'accepted', 'pending', 'inProgress', 'fixed'],
                      }
                    : undefined
            }
            sort={{ field: 'dueDate', order: 'ASC' }}
            actions={<ListActions view={view} onViewChange={setView} />}
            filter={listFilter}
            paginationClassName='pagination-rounded-bottom'
            disablePagination={view === 'map'}
        >
            {view === 'list' ? (
                <CustomDatagridConfigurable
                    header={<CustomDatagridHeader />}
                    bulkActionButtons={bulkActionButtons}
                    className='responsive-table'
                    rowClick={isDesktop ? handleRowClick : false}
                >
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.serviceRequest.labels.requestTitle')}
                                subTitle={translate('resources.serviceInspection.labels.requestSubtitle')}
                            />
                        }
                        sortBy='priority'
                        render={record => <ServiceRequestIdField record={record} resource='service-inspection' />}
                    />
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('common.collectionPoint')}
                                subTitle={translate('resources.serviceRequest.labels.collectionPointSubtitle')}
                            />
                        }
                        sortBy='collectionPoint.customId'
                        render={record =>
                            record?.collectionPoint ? <DeviceCollectionPointField collectionPoint={record.collectionPoint} /> : <UnassignedField />
                        }
                    />
                    <FunctionField
                        label={translate('resources.serviceInspection.labels.dueDate')}
                        sortBy='dueDate'
                        render={record =>
                            record.dueDate ? (
                                <ServiceDate formatSince={record.dueDate} type={record.type} />
                            ) : (
                                <UnassignedField label={translate('resources.serviceInspection.labels.noTerm')} />
                            )
                        }
                    />
                    <FunctionField
                        label={translate('resources.serviceInspection.labels.plannedDate')}
                        sortBy='scheduledDate'
                        render={record => {
                            if (record.scheduledDate) {
                                return <ServiceDate formatSince={record.scheduledDate} />;
                            }
                            const isOverdue = record.dueDate && isBefore(startOfDay(new Date(record.dueDate)), startOfDay(new Date()));
                            return (
                                <UnassignedField
                                    label={translate('resources.serviceInspection.labels.noTerm')}
                                    sx={isOverdue ? { color: Palette.red.red900 } : undefined}
                                />
                            );
                        }}
                    />
                    <FunctionField
                        className='service-request-serial-number'
                        label={<TableHeaderCellWithSubtitle title={translate('resources.device.fields.serialNumber')} />}
                        source='device.serialNumber'
                        sortBy='device.serialNumber'
                        onClick={e => e.stopPropagation()}
                        render={record =>
                            record?.device?.id && record?.device?.serialNumber ? (
                                <DrawerLink resource='device' action='show' source={record.device.id} label={record.device.serialNumber} relative />
                            ) : (
                                <UnassignedField />
                            )
                        }
                    />
                    <FunctionField
                        label={<TableHeaderCellWithSubtitle title={translate('common.status')} subTitle={translate('resources.product.fields.lastUpdate')} />}
                        sortBy='updatedAt'
                        className='service-request-status'
                        render={record =>
                            record.status === 'new' ? (
                                <DeviceServiceStatusField
                                    className='device-status-field device-status-field-last service-request-device-status-field'
                                    formatSince={record.updatedAt}
                                    statusText={translate('resources.serviceRequest.labels.statusNew')}
                                    handleClick={() => handleRowClick(record.id)}
                                />
                            ) : (
                                <ServiceRequestStatusField record={record} handleClick={() => handleRowClick(record.id)} />
                            )
                        }
                    />
                    <FunctionField
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.serviceRequest.labels.coordinator')}
                                subTitle={translate('resources.serviceRequest.labels.serviceman')}
                            />
                        }
                        className='service-request-coordinator'
                        sortBy='coordinator.lastName'
                        render={record => <ServiceRequestUsersField record={record} />}
                    />
                </CustomDatagridConfigurable>
            ) : (
                <ServiceInspectionMap listFilter={listFilter} bulkActionButtons={bulkActionButtons} />
            )}
        </StyledServiceList>
    );
};

const StyledServiceInspectionCreate = styled(Box)(globalStyles.serviceForm);

const DEVICE_RESOURCES_FOR_SERVICE_CREATE = new Set(['device', 'device-issue']);

export const ServiceInspectionCreate = ({ handleClose = () => {}, elementId }) => {
    const resource = useResourceContext();
    const shouldFetchDevice = !!elementId && DEVICE_RESOURCES_FOR_SERVICE_CREATE.has(resource);
    const { data, isLoading } = useGetOne('device', { id: elementId }, { enabled: shouldFetchDevice });
    const translate = useTranslate();

    const recordFromDevice = data ? { collectionPointId: data.collectionPointId ?? data.collectionPoint?.id, deviceId: data.id } : {};

    if (isLoading) return null;

    return (
        <StyledServiceInspectionCreate>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.serviceRequest.labels.createServiceInspectionTitle')}</Typography>
                    <Box>
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <CustomCreate handleClose={handleClose} record={recordFromDevice} resource='service-inspection'>
                <Box className='padding-wrapper'>
                    <ServiceForm resource='service-inspection' />
                </Box>
            </CustomCreate>
        </StyledServiceInspectionCreate>
    );
};

export const ServiceInspectionEdit = ({ handleClose = () => {}, elementId }) => {
    const { data, isLoading } = useGetOne('service-inspection', { id: elementId });
    const translate = useTranslate();

    const formattedId = data?.customId?.toString().padStart(6, '0') || '';

    if (isLoading) return null;

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.serviceInspection.labels.editServiceInspectionTitle', { id: formattedId })}>
            <Edit
                id={elementId}
                resource='service-inspection'
                actions={false}
                mutationMode='pessimistic'
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <ServiceShow handleClose={handleClose} elementId={elementId} resource='service-inspection' />
            </Edit>
        </RightDrawer>
    );
};

const ListActions = ({ view, onViewChange }) => {
    const { permissions } = usePermissions();
    const translate = useTranslate();

    return (
        <TopToolbar>
            <SearchFilterInput />
            <StyledViewToggle
                value={view}
                exclusive
                size='small'
                onChange={(_, nextView) => {
                    if (nextView) {
                        onViewChange(nextView);
                    }
                }}
            >
                <ToggleButton value='list'>
                    <span className='toggle-button-content'>
                        <TableIcon />
                        {translate('ra.action.list')}
                    </span>
                </ToggleButton>
                <ToggleButton value='map'>
                    <span className='toggle-button-content'>
                        <MapIcon />
                        {translate('common.map.label')}
                    </span>
                </ToggleButton>
            </StyledViewToggle>
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.serviceRequest.labels.createServiceInspection')} />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

ServiceInspectionCreate.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

ServiceInspectionEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

ListActions.propTypes = {
    view: PropTypes.string.isRequired,
    onViewChange: PropTypes.func.isRequired,
};
